
import React, { useState } from 'react';
import { ComparisonResult, SummaryDifference } from '@/types/quick-compare';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ComparisonStatusAlert } from './results/ComparisonStatusAlert';
import { SummaryTab } from './results/SummaryTab';
import { AddedItemsTab } from './results/AddedItemsTab';
import { RemovedItemsTab } from './results/RemovedItemsTab';
import { ChangedItemsTab } from './results/ChangedItemsTab';
import { formatLineItemsForDisplay } from '@/utils/estimates/compareEstimates';

interface ComparisonResultsProps {
  results: ComparisonResult;
}

export const ComparisonResults: React.FC<ComparisonResultsProps> = ({ results }) => {
  const [activeTab, setActiveTab] = useState('summary');
  
  const { summary, isIdentical, summaryDifferences = [] } = results;
  const formattedItems = formatLineItemsForDisplay(results.differences);
  
  // Filter items that actually have changes (not just calculated changes)
  const actualChangedItems = React.useMemo(() => {
    return formattedItems.changed.filter(item => {
      if (!item.changedFields || item.changedFields.length === 0) return false;
      
      return item.changedFields.some(field => 
        item.original[field as keyof typeof item.original] !== 
        item.modified[field as keyof typeof item.modified]
      );
    });
  }, [formattedItems.changed]);
  
  // Count of items that have actual changes
  const actualChangedCount = actualChangedItems.length;
  
  // Get accurate count of summary differences
  const summaryDiffCount = summaryDifferences?.length || 0;
  
  // Sum the actual counts of changed items for an accurate total
  const diffTotal = summary.addedItems + summary.removedItems + actualChangedCount;
  const totalChanges = diffTotal + summaryDiffCount;
  
  return (
    <div className="space-y-6">
      {/* Status Alert */}
      <ComparisonStatusAlert 
        isIdentical={isIdentical}
        differenceCount={totalChanges}
        priceDifference={summary.priceDifference}
        summaryChanges={summaryDiffCount}
      />

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="summary">
            Summary {summaryDiffCount > 0 && `(${summaryDiffCount})`}
          </TabsTrigger>
          <TabsTrigger value="added" disabled={summary.addedItems === 0}>
            Added ({summary.addedItems})
          </TabsTrigger>
          <TabsTrigger value="removed" disabled={summary.removedItems === 0}>
            Removed ({summary.removedItems})
          </TabsTrigger>
          <TabsTrigger value="changed" disabled={actualChangedCount === 0}>
            Changed ({actualChangedCount})
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Content */}
        <TabsContent value="summary">
          <SummaryTab results={results} />
        </TabsContent>
        
        <TabsContent value="added">
          <AddedItemsTab 
            addedItems={formattedItems.added} 
            count={summary.addedItems} 
          />
        </TabsContent>
        
        <TabsContent value="removed">
          <RemovedItemsTab 
            removedItems={formattedItems.removed} 
            count={summary.removedItems} 
          />
        </TabsContent>
        
        <TabsContent value="changed">
          <ChangedItemsTab 
            changedItems={actualChangedItems}
            count={actualChangedCount}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
